import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, ImageIcon, XIcon, PackageIcon, CheckIcon } from 'lucide-react';
const materialCategories = [{
  id: 'meat',
  name: 'Fleisch'
}, {
  id: 'spices',
  name: 'Gewürze'
}, {
  id: 'casings',
  name: 'Därme'
}, {
  id: 'additives',
  name: 'Zusatzstoffe'
}] as const;
const initialMaterials = [{
  id: 'M001',
  productNumber: 'FL-001',
  name: 'Schweineschulter',
  description: 'Qualitätssicherung und Tierwohl zertifizierte Schweineschulter',
  category: 'meat',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5e6Fz2qpm3qzkuePx7CjBr/ChatGPT_Image_5._Mai_2025%2C_09_23_28.png',
  stock: 500,
  unit: 'kg',
  minStock: 100,
  supplier: 'Tönnies Lebensmittel GmbH',
  storageConditions: 'Gekühlt bei 2-4°C',
  productionStation: 'kutterei',
  isWeighed: true,
  weight: 25,
  weightTolerance: 2
}, {
  id: 'M002',
  productNumber: 'ZS-001',
  name: 'Biostart Classic',
  description: 'Standardstarterkulturen für die Rohwurstreifung',
  category: 'additives',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/s71Uy9gZiK4GMq3EQbbx77/ChatGPT_Image_5._Mai_2025%2C_09_25_52.png',
  stock: 250,
  unit: 'kg',
  minStock: 50,
  supplier: 'Chr. Hansen GmbH',
  storageConditions: 'Gekühlt bei 2-8°C',
  productionStation: 'kutterei',
  isWeighed: true,
  weight: 0.25,
  weightTolerance: 5
}, {
  id: 'M003',
  productNumber: '94009',
  name: 'Landjäger Gewürz Premium',
  description: 'Premium Gewürzmischung für Landjäger',
  category: 'spices',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7N1i6Kmie7LLk21tDRpNXK/ChatGPT_Image_5._Mai_2025%2C_09_28_02.png',
  stock: 4.88,
  unit: 'kg',
  minStock: 2,
  supplier: 'Raps GmbH',
  storageConditions: 'Trocken und kühl lagern',
  productionStation: 'kutterei',
  isWeighed: true,
  weight: 1.5,
  weightTolerance: 1
}, {
  id: 'M004',
  productNumber: 'NPS-001',
  name: 'Nitritpökelsalz 0,9%',
  description: 'Nitritpökelsalz für die Fleischverarbeitung',
  category: 'additives',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nhSATU99ddDGaFGCQw31KU/ChatGPT_Image_5._Mai_2025%2C_09_29_45.png',
  stock: 0.5,
  unit: 'kg',
  minStock: 0.2,
  supplier: 'Südsalz GmbH',
  storageConditions: 'Trocken lagern',
  productionStation: 'kutterei',
  isWeighed: true,
  weight: 0.5,
  weightTolerance: 1
}] as const;
export function MaterialsManagement() {
  const [activeTab, setActiveTab] = useState<'materials' | 'recipes'>('materials');
  const [materials, setMaterials] = useState(initialMaterials);
  return <div className="p-6 h-full" data-id="element-113">
      <header className="mb-8" data-id="element-114">
        <h1 className="text-3xl font-bold text-[#e20019]" data-id="element-115">
          Rohstoff- & Rezeptverwaltung
        </h1>
        <p className="text-gray-600" data-id="element-116">
          Verwalten Sie Ihre Rohstoffe und Bestände
        </p>
      </header>
      <div className="mb-6 border-b border-gray-200" data-id="element-117">
        <div className="flex gap-4" data-id="element-118">
          <button onClick={() => setActiveTab('materials')} className={`px-4 py-2 font-medium text-sm relative ${activeTab === 'materials' ? 'text-[#ec8301]' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-119">
            Rohstoffe
            {activeTab === 'materials' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#ec8301]" data-id="element-120" />}
          </button>
          <button onClick={() => setActiveTab('recipes')} className={`px-4 py-2 font-medium text-sm relative ${activeTab === 'recipes' ? 'text-[#ec8301]' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-121">
            Rezepte
            {activeTab === 'recipes' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#ec8301]" data-id="element-122" />}
          </button>
        </div>
      </div>
      {activeTab === 'materials' ? <MaterialsView materials={materials} setMaterials={setMaterials} data-id="element-123" /> : <RecipeManagement materials={materials} data-id="element-124" />}
    </div>;
}
function MaterialsView({
  materials,
  setMaterials
}: {
  materials: typeof initialMaterials;
  setMaterials: React.Dispatch<React.SetStateAction<typeof initialMaterials>>;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState<(typeof initialMaterials)[0] | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const handleNewMaterial = () => {
    setSelectedMaterial({
      id: `M${String(materials.length + 1).padStart(3, '0')}`,
      productNumber: '',
      name: '',
      description: '',
      category: 'meat',
      image: '',
      stock: 0,
      unit: 'kg',
      minStock: 0,
      supplier: '',
      storageConditions: '',
      productionStation: '',
      isWeighed: false,
      weight: 0,
      weightTolerance: 5
    });
    setIsEditing(true);
  };
  const filteredMaterials = materials.filter(material => {
    const matchesSearch = material.name.toLowerCase().includes(searchTerm.toLowerCase()) || material.productNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || material.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });
  return <div className="flex flex-col lg:flex-row gap-6 h-[calc(100%-8rem)]" data-id="element-125">
      <div className="lg:w-1/3 flex flex-col" data-id="element-126">
        <div className="mb-4 space-y-4" data-id="element-127">
          <div className="flex gap-2" data-id="element-128">
            <div className="relative flex-1" data-id="element-129">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-130" />
              <input type="text" placeholder="Rohstoff suchen..." className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec8301]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-131" />
            </div>
            <button className="bg-[#ec8301] hover:bg-[#e20019] text-white rounded-lg px-4 py-2 flex items-center transition-colors" onClick={handleNewMaterial} data-id="element-132">
              <PlusIcon className="h-5 w-5 mr-1" data-id="element-133" />
              <span data-id="element-134">Neu</span>
            </button>
          </div>
          <select className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#ec8301]" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} data-id="element-135">
            <option value="all" data-id="element-136">Alle Kategorien</option>
            {materialCategories.map(category => <option key={category.id} value={category.id} data-id="element-137">
                {category.name}
              </option>)}
          </select>
        </div>
        <div className="bg-white rounded-lg shadow flex-1 overflow-auto" data-id="element-138">
          {filteredMaterials.length === 0 ? <div className="p-4 text-center text-gray-500" data-id="element-139">
              Keine Rohstoffe gefunden
            </div> : <ul className="divide-y divide-gray-200" data-id="element-140">
              {filteredMaterials.map(material => <li key={material.id} data-id="element-141">
                  <button className={`w-full text-left p-4 hover:bg-gray-50 ${selectedMaterial?.id === material.id ? 'bg-gray-100' : ''}`} onClick={() => {
              setSelectedMaterial(material);
              setIsEditing(false);
            }} data-id="element-142">
                    <div className="flex gap-3" data-id="element-143">
                      <div className="h-16 w-16 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0" data-id="element-144">
                        {material.image ? <img src={material.image} alt={material.name} className="h-full w-full object-cover" data-id="element-145" /> : <div className="h-full w-full flex items-center justify-center" data-id="element-146">
                            <PackageIcon className="h-8 w-8 text-gray-400" data-id="element-147" />
                          </div>}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-148">
                        <div className="flex items-center gap-2" data-id="element-149">
                          <span className="font-medium text-gray-900" data-id="element-150">
                            {material.name}
                          </span>
                          <span className="text-sm text-[#ec8301]" data-id="element-151">
                            {material.productNumber}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1" data-id="element-152">
                          Bestand: {material.stock} {material.unit}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-153">
                          {materialCategories.find(c => c.id === material.category)?.name}
                        </div>
                      </div>
                    </div>
                  </button>
                </li>)}
            </ul>}
        </div>
      </div>
      <div className="lg:w-2/3 bg-white rounded-lg shadow p-6 overflow-auto" data-id="element-154">
        {!selectedMaterial ? <div className="h-full flex items-center justify-center text-gray-500" data-id="element-155">
            <p data-id="element-156">Wählen Sie einen Rohstoff aus oder erstellen Sie einen neuen</p>
          </div> : isEditing ? <MaterialForm material={selectedMaterial} onSave={updatedMaterial => {
        if (materials.some(m => m.id === updatedMaterial.id)) {
          setMaterials(materials.map(m => m.id === updatedMaterial.id ? updatedMaterial : m));
        } else {
          setMaterials([...materials, updatedMaterial]);
        }
        setSelectedMaterial(updatedMaterial);
        setIsEditing(false);
      }} onCancel={() => {
        if (selectedMaterial.name === '') {
          setSelectedMaterial(null);
        }
        setIsEditing(false);
      }} data-id="element-157" /> : <MaterialDetails material={selectedMaterial} onEdit={() => setIsEditing(true)} onDelete={() => {
        setMaterials(materials.filter(m => m.id !== selectedMaterial.id));
        setSelectedMaterial(null);
      }} data-id="element-158" />}
      </div>
    </div>;
}
function RecipeManagement({
  materials
}: {
  materials: typeof initialMaterials;
}) {
  const [recipes, setRecipes] = useState([{
    id: 'R001',
    name: 'Pfefferbeißer Rezept',
    description: 'Traditionelles Rezept für Pfefferbeißer',
    ingredients: [{
      materialId: 'M001',
      quantity: 80,
      unit: '%'
    }, {
      materialId: 'M002',
      quantity: 0.5,
      unit: '%'
    }, {
      materialId: 'M003',
      quantity: 2,
      unit: '%'
    }]
  }]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState<null | (typeof recipes)[0]>(null);
  const [isEditing, setIsEditing] = useState(false);
  const handleNewRecipe = () => {
    setSelectedRecipe({
      id: `R${String(recipes.length + 1).padStart(3, '0')}`,
      name: '',
      description: '',
      ingredients: []
    });
    setIsEditing(true);
  };
  return <div className="flex flex-col lg:flex-row gap-6 h-[calc(100%-8rem)]" data-id="element-159">
      <div className="lg:w-1/3 flex flex-col" data-id="element-160">
        <div className="mb-4 space-y-4" data-id="element-161">
          <div className="flex gap-2" data-id="element-162">
            <div className="relative flex-1" data-id="element-163">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-164" />
              <input type="text" placeholder="Rezept suchen..." className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec8301]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-165" />
            </div>
            <button onClick={handleNewRecipe} className="bg-[#ec8301] hover:bg-[#e20019] text-white rounded-lg px-4 py-2 flex items-center transition-colors" data-id="element-166">
              <PlusIcon className="h-5 w-5 mr-1" data-id="element-167" />
              <span data-id="element-168">Neu</span>
            </button>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow flex-1 overflow-auto" data-id="element-169">
          <ul className="divide-y divide-gray-200" data-id="element-170">
            {recipes.filter(recipe => recipe.name.toLowerCase().includes(searchTerm.toLowerCase())).map(recipe => <li key={recipe.id} data-id="element-171">
                  <button className={`w-full text-left p-4 hover:bg-gray-50 ${selectedRecipe?.id === recipe.id ? 'bg-gray-100' : ''}`} onClick={() => {
              setSelectedRecipe(recipe);
              setIsEditing(false);
            }} data-id="element-172">
                    <div className="flex gap-3" data-id="element-173">
                      <div className="flex-1 min-w-0" data-id="element-174">
                        <div className="flex items-center gap-2" data-id="element-175">
                          <span className="font-medium text-gray-900" data-id="element-176">
                            {recipe.name}
                          </span>
                          <span className="text-sm text-[#ec8301]" data-id="element-177">
                            {recipe.id}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1" data-id="element-178">
                          {recipe.ingredients.length} Zutaten
                        </div>
                      </div>
                    </div>
                  </button>
                </li>)}
          </ul>
        </div>
      </div>
      <div className="lg:w-2/3 bg-white rounded-lg shadow p-6 overflow-auto" data-id="element-179">
        {!selectedRecipe ? <div className="h-full flex items-center justify-center text-gray-500" data-id="element-180">
            <p data-id="element-181">Wählen Sie ein Rezept aus oder erstellen Sie ein neues</p>
          </div> : isEditing ? <RecipeForm recipe={selectedRecipe} materials={materials} onSave={updatedRecipe => {
        if (recipes.some(r => r.id === updatedRecipe.id)) {
          setRecipes(recipes.map(r => r.id === updatedRecipe.id ? updatedRecipe : r));
        } else {
          setRecipes([...recipes, updatedRecipe]);
        }
        setSelectedRecipe(updatedRecipe);
        setIsEditing(false);
      }} onCancel={() => {
        if (selectedRecipe.name === '') {
          setSelectedRecipe(null);
        }
        setIsEditing(false);
      }} data-id="element-182" /> : <RecipeDetails recipe={selectedRecipe} materials={materials} onEdit={() => setIsEditing(true)} onDelete={() => {
        setRecipes(recipes.filter(r => r.id !== selectedRecipe.id));
        setSelectedRecipe(null);
      }} data-id="element-183" />}
      </div>
    </div>;
}
interface RecipeFormProps {
  recipe: {
    id: string;
    name: string;
    description: string;
    ingredients: Array<{
      materialId: string;
      quantity: number;
      unit: string;
    }>;
  };
  materials: typeof initialMaterials;
  onSave: (recipe: RecipeFormProps['recipe']) => void;
  onCancel: () => void;
}
function RecipeForm({
  recipe,
  materials,
  onSave,
  onCancel
}: RecipeFormProps) {
  const [formData, setFormData] = useState(recipe);
  const [searchTerm, setSearchTerm] = useState('');
  const filteredMaterials = materials.filter(material => material.name.toLowerCase().includes(searchTerm.toLowerCase()) || material.productNumber.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleAddIngredient = (material: (typeof materials)[0]) => {
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, {
        materialId: material.id,
        quantity: material.weight || 0,
        unit: '%'
      }]
    });
    setSearchTerm('');
  };
  const handleRemoveIngredient = (index: number) => {
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter((_, i) => i !== index)
    });
  };
  const totalPercentage = formData.ingredients.reduce((sum, ing) => sum + ing.quantity, 0);
  return <form onSubmit={e => {
    e.preventDefault();
    onSave(formData);
  }} className="space-y-6" data-id="element-184">
      <div data-id="element-185">
        <h2 className="text-2xl font-bold mb-4" data-id="element-186">
          {formData.id ? 'Rezept bearbeiten' : 'Neues Rezept'}
        </h2>
      </div>
      <div className="space-y-4" data-id="element-187">
        <div data-id="element-188">
          <label className="block text-sm font-medium mb-1" data-id="element-189">Rezeptname</label>
          <input type="text" value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} className="w-full border border-gray-300 rounded-lg p-2" required data-id="element-190" />
        </div>
        <div data-id="element-191">
          <label className="block text-sm font-medium mb-1" data-id="element-192">Beschreibung</label>
          <textarea value={formData.description} onChange={e => setFormData({
          ...formData,
          description: e.target.value
        })} className="w-full border border-gray-300 rounded-lg p-2" rows={3} data-id="element-193" />
        </div>
        <div data-id="element-194">
          <h3 className="text-lg font-medium mb-4" data-id="element-195">Zutaten</h3>
          <div className="space-y-4" data-id="element-196">
            <div className="flex-1" data-id="element-197">
              <div className="relative" data-id="element-198">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-199" />
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Zutat suchen..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg" data-id="element-200" />
              </div>
              {searchTerm && <div className="mt-2 border border-gray-200 rounded-lg max-h-40 overflow-y-auto" data-id="element-201">
                  {filteredMaterials.length > 0 ? filteredMaterials.map(material => <div key={material.id} className="p-2 hover:bg-gray-50 cursor-pointer flex items-center justify-between" onClick={() => handleAddIngredient(material)} data-id="element-202">
                        <div data-id="element-203">
                          <div className="font-medium" data-id="element-204">{material.name}</div>
                          <div className="text-sm text-gray-500" data-id="element-205">
                            {material.productNumber}
                          </div>
                        </div>
                        <button type="button" className="text-[#ec8301] hover:text-[#ec8301]/90" data-id="element-206">
                          <PlusIcon className="h-5 w-5" data-id="element-207" />
                        </button>
                      </div>) : <div className="p-2 text-gray-500 text-center" data-id="element-208">
                      Keine Zutaten gefunden
                    </div>}
                </div>}
            </div>
            <div className="bg-gray-50 rounded-lg overflow-hidden" data-id="element-209">
              <table className="w-full" data-id="element-210">
                <thead className="bg-gray-100" data-id="element-211">
                  <tr data-id="element-212">
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600" data-id="element-213">
                      Zutat
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600" data-id="element-214">
                      Menge
                    </th>
                    <th className="px-4 py-2 text-right text-sm font-medium text-gray-600" data-id="element-215"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-216">
                  {formData.ingredients.map((ingredient, index) => <tr key={index} data-id="element-217">
                      <td className="px-4 py-2" data-id="element-218">
                        {materials.find(m => m.id === ingredient.materialId)?.name}
                      </td>
                      <td className="px-4 py-2" data-id="element-219">
                        {ingredient.quantity}
                        {ingredient.unit}
                      </td>
                      <td className="px-4 py-2 text-right" data-id="element-220">
                        <button type="button" onClick={() => handleRemoveIngredient(index)} className="text-red-500 hover:text-red-700" data-id="element-221">
                          <XIcon className="h-5 w-5" data-id="element-222" />
                        </button>
                      </td>
                    </tr>)}
                  <tr className="bg-gray-50" data-id="element-223">
                    <td className="px-4 py-2 font-medium" data-id="element-224">Gesamt</td>
                    <td className="px-4 py-2" data-id="element-225">
                      {totalPercentage}%
                      {Math.abs(totalPercentage - 100) > 0.01 && <span className={`ml-2 text-sm ${totalPercentage > 100 ? 'text-red-500' : 'text-yellow-500'}`} data-id="element-226">
                          {totalPercentage > 100 ? '(Über 100%)' : '(Unter 100%)'}
                        </span>}
                    </td>
                    <td data-id="element-227"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-6 border-t border-gray-200" data-id="element-228">
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-229">
          Abbrechen
        </button>
        <button type="submit" className="px-4 py-2 bg-[#ec8301] text-white rounded-lg hover:bg-[#ec8301]/90" data-id="element-230">
          Speichern
        </button>
      </div>
    </form>;
}
function RecipeDetails({
  recipe,
  materials,
  onEdit,
  onDelete
}: {
  recipe: RecipeFormProps['recipe'];
  materials: typeof initialMaterials;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return <div className="space-y-6" data-id="element-231">
      <div className="flex justify-between items-start" data-id="element-232">
        <div data-id="element-233">
          <h2 className="text-2xl font-bold" data-id="element-234">{recipe.name}</h2>
          <p className="text-gray-500" data-id="element-235">Rezeptnummer: {recipe.id}</p>
        </div>
        <div className="flex gap-2" data-id="element-236">
          <button onClick={onEdit} className="bg-[#ec8301] text-white rounded-lg p-2 hover:bg-[#ec8301]/90" data-id="element-237">
            <EditIcon className="h-5 w-5" data-id="element-238" />
          </button>
          <button onClick={onDelete} className="bg-gray-100 hover:bg-red-100 text-gray-700 hover:text-red-500 rounded-lg p-2" data-id="element-239">
            <TrashIcon className="h-5 w-5" data-id="element-240" />
          </button>
        </div>
      </div>
      {recipe.description && <div data-id="element-241">
          <h3 className="text-lg font-medium mb-2" data-id="element-242">Beschreibung</h3>
          <p className="text-gray-700" data-id="element-243">{recipe.description}</p>
        </div>}
      <div data-id="element-244">
        <h3 className="text-lg font-medium mb-2" data-id="element-245">Zutaten</h3>
        <div className="bg-gray-50 rounded-lg overflow-hidden" data-id="element-246">
          <table className="w-full" data-id="element-247">
            <thead className="bg-gray-100" data-id="element-248">
              <tr data-id="element-249">
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600" data-id="element-250">
                  Zutat
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600" data-id="element-251">
                  Artikelnummer
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600" data-id="element-252">
                  Menge
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-253">
              {recipe.ingredients.map((ingredient, index) => {
              const material = materials.find(m => m.id === ingredient.materialId);
              return <tr key={index} data-id="element-254">
                    <td className="px-4 py-2" data-id="element-255">{material?.name}</td>
                    <td className="px-4 py-2" data-id="element-256">{material?.productNumber}</td>
                    <td className="px-4 py-2" data-id="element-257">
                      {ingredient.quantity}
                      {ingredient.unit}
                    </td>
                  </tr>;
            })}
              <tr className="bg-gray-50" data-id="element-258">
                <td className="px-4 py-2 font-medium" data-id="element-259">Gesamt</td>
                <td data-id="element-260"></td>
                <td className="px-4 py-2" data-id="element-261">
                  {recipe.ingredients.reduce((sum, ing) => sum + ing.quantity, 0)}
                  %
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
interface MaterialDetailsProps {
  material: (typeof initialMaterials)[0];
  onEdit: () => void;
  onDelete: () => void;
}
function MaterialDetails({
  material,
  onEdit,
  onDelete
}: MaterialDetailsProps) {
  const categoryName = materialCategories.find(c => c.id === material.category)?.name;
  return <div className="space-y-6" data-id="element-262">
      <div className="flex justify-between items-start" data-id="element-263">
        <div data-id="element-264">
          <h2 className="text-2xl font-bold" data-id="element-265">{material.name}</h2>
          <p className="text-gray-500" data-id="element-266">
            Artikelnummer: {material.productNumber}
          </p>
        </div>
        <div className="flex gap-2" data-id="element-267">
          <button onClick={onEdit} className="bg-[#ec8301] text-white rounded-lg p-2 hover:bg-[#ec8301]/90" data-id="element-268">
            <EditIcon className="h-5 w-5" data-id="element-269" />
          </button>
          <button onClick={onDelete} className="bg-gray-100 hover:bg-red-100 text-gray-700 hover:text-red-500 rounded-lg p-2" data-id="element-270">
            <TrashIcon className="h-5 w-5" data-id="element-271" />
          </button>
        </div>
      </div>
      <div className="h-48 bg-gray-100 rounded-lg overflow-hidden" data-id="element-272">
        {material.image ? <img src={material.image} alt={material.name} className="w-full h-full object-cover" data-id="element-273" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-274">
            <PackageIcon className="h-12 w-12 text-gray-400" data-id="element-275" />
          </div>}
      </div>
      <div className="grid grid-cols-2 gap-6" data-id="element-276">
        <div data-id="element-277">
          <h3 className="text-lg font-medium mb-2" data-id="element-278">Details</h3>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2" data-id="element-279">
            <div data-id="element-280">
              <span className="text-gray-600" data-id="element-281">Kategorie:</span>
              <span className="ml-2 font-medium" data-id="element-282">{categoryName}</span>
            </div>
            <div data-id="element-283">
              <span className="text-gray-600" data-id="element-284">Lieferant:</span>
              <span className="ml-2 font-medium" data-id="element-285">{material.supplier}</span>
            </div>
          </div>
        </div>
      </div>
      {material.description && <div data-id="element-286">
          <h3 className="text-lg font-medium mb-2" data-id="element-287">Beschreibung</h3>
          <p className="text-gray-700" data-id="element-288">{material.description}</p>
        </div>}
      {(material.productionStation || material.isWeighed) && <div data-id="element-289">
          <h3 className="text-lg font-medium mb-2" data-id="element-290">Produktionsdetails</h3>
          <div className="bg-gray-50 rounded-lg p-4 space-y-4" data-id="element-291">
            {material.productionStation && <div data-id="element-292">
                <span className="text-gray-600" data-id="element-293">Produktionsstation:</span>
                <span className="ml-2 font-medium capitalize" data-id="element-294">
                  {material.productionStation}
                </span>
              </div>}
            {material.isWeighed && <>
                <div className="flex items-center" data-id="element-295">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-296" />
                  <span className="text-gray-700" data-id="element-297">
                    Wird an Produktionsstation verwogen
                  </span>
                </div>
                {material.weight && <div data-id="element-298">
                    <span className="text-gray-600" data-id="element-299">Sollgewicht:</span>
                    <span className="ml-2 font-medium" data-id="element-300">
                      {material.weight} {material.unit}
                    </span>
                  </div>}
                {material.weightTolerance && <div data-id="element-301">
                    <span className="text-gray-600" data-id="element-302">Toleranz:</span>
                    <span className="ml-2 font-medium" data-id="element-303">
                      ±{material.weightTolerance}%
                    </span>
                  </div>}
              </>}
          </div>
        </div>}
    </div>;
}
interface MaterialFormProps {
  material: Omit<(typeof initialMaterials)[0], 'minStock' | 'stock' | 'storageConditions'> & {
    productionStation?: string;
    isWeighed?: boolean;
    weight?: number;
    weightTolerance?: number;
  };
  onSave: (material: Omit<(typeof initialMaterials)[0], 'minStock' | 'stock' | 'storageConditions'> & {
    productionStation?: string;
    isWeighed?: boolean;
    weight?: number;
    weightTolerance?: number;
  }) => void;
  onCancel: () => void;
}
function MaterialForm({
  material,
  onSave,
  onCancel
}: MaterialFormProps) {
  const [formData, setFormData] = useState({
    ...material,
    productionStation: material.productionStation || '',
    isWeighed: material.isWeighed || false,
    weight: material.weight || 0,
    weightTolerance: material.weightTolerance || 5
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value
    });
  };
  // Calculate weight range based on tolerance
  const calculateWeightRange = () => {
    if (!formData.weight || !formData.weightTolerance) return null;
    const tolerance = formData.weight * (formData.weightTolerance / 100);
    return {
      min: (formData.weight - tolerance).toFixed(2),
      max: (formData.weight + tolerance).toFixed(2)
    };
  };
  const weightRange = calculateWeightRange();
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setFormData({
        ...formData,
        image: imageUrl
      });
    }
  };
  return <form onSubmit={e => {
    e.preventDefault();
    onSave(formData);
  }} className="space-y-6" data-id="element-304">
      <div data-id="element-305">
        <h2 className="text-2xl font-bold mb-4" data-id="element-306">
          {formData.id ? 'Rohstoff bearbeiten' : 'Neuer Rohstoff'}
        </h2>
      </div>
      <div data-id="element-307">
        <h3 className="text-lg font-medium mb-4" data-id="element-308">Produktbild</h3>
        <div className="border border-gray-300 rounded-lg overflow-hidden" data-id="element-309">
          {formData.image ? <div className="relative" data-id="element-310">
              <img src={formData.image} alt="Vorschau" className="w-full h-64 object-cover" data-id="element-311" />
              <button type="button" onClick={() => setFormData({
            ...formData,
            image: ''
          })} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600" data-id="element-312">
                <XIcon className="h-4 w-4" data-id="element-313" />
              </button>
            </div> : <div className="p-6 text-center h-64 flex flex-col items-center justify-center bg-gray-50" data-id="element-314">
              <ImageIcon className="h-12 w-12 text-gray-400 mb-3" data-id="element-315" />
              <label className="cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#ec8301] hover:bg-[#ec8301]/90" data-id="element-316">
                <span data-id="element-317">Bild hochladen</span>
                <input type="file" className="sr-only" accept="image/*" onChange={handleFileUpload} data-id="element-318" />
              </label>
              <p className="text-xs text-gray-500 mt-2" data-id="element-319">PNG, JPG bis 10MB</p>
            </div>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4" data-id="element-320">
        <div data-id="element-321">
          <label className="block text-sm font-medium mb-1" data-id="element-322">
            Artikelnummer
          </label>
          <input type="text" name="productNumber" value={formData.productNumber} onChange={handleInputChange} className="w-full border border-gray-300 rounded-lg p-2" required data-id="element-323" />
        </div>
        <div data-id="element-324">
          <label className="block text-sm font-medium mb-1" data-id="element-325">Name</label>
          <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full border border-gray-300 rounded-lg p-2" required data-id="element-326" />
        </div>
      </div>
      <div data-id="element-327">
        <label className="block text-sm font-medium mb-1" data-id="element-328">Beschreibung</label>
        <textarea name="description" value={formData.description} onChange={handleInputChange} className="w-full border border-gray-300 rounded-lg p-2" rows={3} data-id="element-329" />
      </div>
      <div className="grid grid-cols-2 gap-4" data-id="element-330">
        <div data-id="element-331">
          <label className="block text-sm font-medium mb-1" data-id="element-332">Kategorie</label>
          <select name="category" value={formData.category} onChange={handleInputChange} className="w-full border border-gray-300 rounded-lg p-2" required data-id="element-333">
            {materialCategories.map(category => <option key={category.id} value={category.id} data-id="element-334">
                {category.name}
              </option>)}
          </select>
        </div>
        <div data-id="element-335">
          <label className="block text-sm font-medium mb-1" data-id="element-336">Einheit</label>
          <input type="text" name="unit" value={formData.unit} onChange={handleInputChange} className="w-full border border-gray-300 rounded-lg p-2" required data-id="element-337" />
        </div>
      </div>
      <div data-id="element-338">
        <label className="block text-sm font-medium mb-1" data-id="element-339">Lieferant</label>
        <input type="text" name="supplier" value={formData.supplier} onChange={handleInputChange} className="w-full border border-gray-300 rounded-lg p-2" data-id="element-340" />
      </div>
      <div className="border-t border-gray-200 pt-6" data-id="element-341">
        <h3 className="text-lg font-medium mb-4" data-id="element-342">Produktionsdetails</h3>
        <div className="space-y-4" data-id="element-343">
          <div data-id="element-344">
            <label className="block text-sm font-medium mb-1" data-id="element-345">
              Produktionsstation
            </label>
            <select name="productionStation" value={formData.productionStation} onChange={handleInputChange} className="w-full border border-gray-300 rounded-lg p-2" data-id="element-346">
              <option value="" data-id="element-347">Bitte wählen...</option>
              <option value="kutterei" data-id="element-348">Kutterei</option>
              <option value="fuellerei" data-id="element-349">Füllerei</option>
              <option value="raeucherei" data-id="element-350">Räucherei</option>
            </select>
          </div>
          <div className="flex items-center gap-2" data-id="element-351">
            <input type="checkbox" id="isWeighed" name="isWeighed" checked={formData.isWeighed} onChange={handleInputChange} className="rounded border-gray-300 text-[#ec8301] focus:ring-[#ec8301]" data-id="element-352" />
            <label htmlFor="isWeighed" className="text-sm font-medium" data-id="element-353">
              Wird an Produktionsstation verwogen
            </label>
          </div>
          {formData.isWeighed && <div className="space-y-4 pl-6" data-id="element-354">
              <div className="grid grid-cols-2 gap-4" data-id="element-355">
                <div data-id="element-356">
                  <label className="block text-sm font-medium mb-1" data-id="element-357">
                    Gewicht
                  </label>
                  <div className="flex gap-2" data-id="element-358">
                    <input type="number" name="weight" value={formData.weight} onChange={handleInputChange} className="w-full border border-gray-300 rounded-lg p-2" step="0.01" data-id="element-359" />
                    <select className="border border-gray-300 rounded-lg p-2" disabled data-id="element-360">
                      <option data-id="element-361">kg</option>
                    </select>
                  </div>
                </div>
                <div data-id="element-362">
                  <label className="block text-sm font-medium mb-1" data-id="element-363">
                    Toleranz (±%)
                  </label>
                  <input type="number" name="weightTolerance" value={formData.weightTolerance} onChange={handleInputChange} className="w-full border border-gray-300 rounded-lg p-2" min="0" max="100" data-id="element-364" />
                </div>
              </div>
              {weightRange && <div className="bg-gray-50 p-3 rounded-lg" data-id="element-365">
                  <p className="text-sm text-gray-600" data-id="element-366">
                    Zulässiger Gewichtsbereich:
                    <span className="font-medium ml-2" data-id="element-367">
                      {weightRange.min} kg - {weightRange.max} kg
                    </span>
                  </p>
                </div>}
            </div>}
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-6 border-t border-gray-200" data-id="element-368">
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-369">
          Abbrechen
        </button>
        <button type="submit" className="px-4 py-2 bg-[#ec8301] text-white rounded-lg hover:bg-[#ec8301]/90" data-id="element-370">
          Speichern
        </button>
      </div>
    </form>;
}